import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { trackEvent } from "@hopper-b2b/api";

import {
  FlightPassengerSelectors,
  ParentState,
  PassengerErrorModalTypes,
  PassengerInformation,
  useChildMachineSelector,
  useChildMachineState,
  useCheckoutStateSelector as useParentMachineSelector,
} from "@hopper-b2b/checkout";
import { useExperiment } from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getDarkModePreferred } from "@hopper-b2b/themes";
import {
  ADD_FREQUENT_FLYER,
  CHOOSE_TRAVELER,
  type IPerson,
  type PersonId,
  SELECT_TRAVELERS,
} from "@hopper-b2b/types";
import {
  ActionLink,
  ErrorPopup,
  IconComponent,
  IconName,
  MobilePopoverCard,
  Snackbar,
} from "@hopper-b2b/ui";
import { useDeviceTypes, useUberBridge } from "@hopper-b2b/utilities";
import clsx from "clsx";
import { FEATURE_FLAGS, UserContext } from "../../../../App";
import Warning from "../../../../assets/client/warning-triangle.svg";
import { PATH_FLIGHTS_SEARCH, apiConfig } from "../../../../utils/urlPaths";
import { Event, type TEvent } from "../../events";
import {
  getChooseTravelerProperties,
  getClientAssets,
  getCurrencyFromPricingInfo,
  getPricing,
} from "../../selectors";
import type { FlightMachineContext } from "../../types";
import { ConnectedBreadcrumbs } from "../Breadcrumbs";
import { ConnectedPriceBreakdownContent } from "./../PriceBreakdownDropdown";
import styles from "./styles.module.scss";

const childMachineID = ParentState.passengerInformation;
const TRAVELER_HASH = "travelerForm";

export const ConnectedPassengerInformation = (props) => {
  const { t } = useI18nContext();
  const { matchesMobile } = useDeviceTypes();
  const userContext = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const { postEvent } = useUberBridge();
  const enableDarkMode = useExperiment(FEATURE_FLAGS.APP_DARKMODE);
  const darkModePreferred = getDarkModePreferred();
  const clientAssets = useParentMachineSelector(getClientAssets);

  const [, send] = useChildMachineState<TEvent, FlightMachineContext>(
    childMachineID
  );

  const isLoading = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getIsPassengerInformationLoading
  );
  const loadingMessage = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getIsPassengerInformationLoading
  )
    ? t("passengerInformation.updatingPassengers")
    : t("passengerInformation.validatingPassengers");
  const userPassengers = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getUserPassengers
  );

  const allSelectedPassengerIds = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getAllSelectedPassengerIds
  );
  const currentPassenger = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getCurrentUserPassenger
  );
  const openPassengerFormModal = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getOpenPassengerFormModal
  );
  const openPassengerPicker = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getOpenPassengerPicker
  );

  // Infant Modal
  const currentInfantId = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getCurrentInfantId
  );
  const isInfantModalLoading = useParentMachineSelector(
    FlightPassengerSelectors.getIsInfantSeatModalLoading
  );

  const chooseTravelerProperties = useParentMachineSelector(
    getChooseTravelerProperties
  );
  const tripPricing = useParentMachineSelector(getPricing);

  const tripCurrency = useParentMachineSelector(getCurrencyFromPricingInfo);

  const travelerWorkflowTitles = {
    travelerInfoTitle: currentPassenger?.id
      ? t("passengerInformation.editPassengerInfoText")
      : matchesMobile
        ? t("passengerInformation.addPassengerInfoText")
        : t("passengerInformationTitle"),
    travelerInfoHeader: t("passengerInformation.title"),
    travelerInfoSubtitle: t("passengerInformation.passengerSubtitle"),
    travelerSelectSubtitle: t("passengerInformation.passengerSelectSubtitle"),
    noTravelersSelectSubtitle: t(
      "passengerInformation.noPassengerSelectSubtitle"
    ),
    frequentFlyerTitle: t("passengerInformation.frequentFlyerTitle"),
    additionalInfoTitle: t("passengerInformation.additionalInfo"),
    adultTitle: t("passengerInformation.adult"),
    childTitle: t("passengerInformation.child"),
    infantSeatTitle: t("passengerInformation.infant"),
    infantLapTitle: t("passengerInformation.infant"),
    addTravelers: t("passengerInformation.addPassengers"),
    buttonMessage: t("addNewPassenger"),
    notification: t("passengerInformation.notification"),
    frequentFlyerNotification: t(
      "passengerInformation.frequentFlyerNotification"
    ),
  };

  const addTravelerFormHash = useCallback(() => {
    history.push(`${location.pathname}${location.search}#${TRAVELER_HASH}`);
  }, [history, location.pathname, location.search]);

  const removeTravelerFormHash = useCallback(() => {
    if (location.hash.includes(TRAVELER_HASH)) {
      history.goBack();
    }
  }, [history, location.hash]);

  const handleCloseForm = useCallback(() => {
    send({ type: Event.OPEN_PASSENGER_PICKER });
  }, [send]);

  const handleContinue = () => {
    removeTravelerFormHash();
    send(Event.NEXT);
  };

  const deletePassenger = () => {
    send({ type: Event.DELETE_PASSENGER, personId: passengerIdToDelete });
    setPassengerIdToDelete(null);
  };

  const [passengerIdToDelete, setPassengerIdToDelete] = useState<string | null>(
    null
  );
  const handleDeletePassenger = (personId: string) => {
    setPassengerIdToDelete(personId);
  };

  const handleEditClick = (passenger: IPerson) => {
    send(Event.OPEN_PASSENGER_FORM);
    send({ type: Event.SET_CURRENT_PASSENGER, passenger });
  };

  const handleGoBack = () => {
    history.goBack();
  };

  const handleSelectPassenger = (
    passengerId: PersonId,
    singleTravelerWorkflow?: boolean
  ) => {
    if (hasError) {
      send(Event.CLEAR_PASSENGER_INFORMATION_ERROR);
    }
    if (!allSelectedPassengerIds.includes(passengerId)) {
      trackEvent({
        eventName: CHOOSE_TRAVELER,
        properties: chooseTravelerProperties,
      });
      trackEvent({
        eventName: SELECT_TRAVELERS,
        properties: {
          num_travelers: allSelectedPassengerIds.length + 1,
        },
      });

      postEvent(CHOOSE_TRAVELER, {
        destination: chooseTravelerProperties.destination,
        origin: chooseTravelerProperties.origin,
        return_date: chooseTravelerProperties.return_date,
        departure_date: chooseTravelerProperties.departure_date,
        trip_type: chooseTravelerProperties.trip_type,
      });
    }
    send({
      type: Event.SELECT_PASSENGER,
      passengerId,
      singleTravelerWorkflow,
    });
  };

  const handleUpdatePassenger = useCallback(
    (passenger: IPerson, onUpdate?: boolean | undefined) => {
      const freqFlyerKeys = Object.keys(passenger.frequentFlyer);
      if (
        freqFlyerKeys.length > 0 &&
        passenger.frequentFlyer !==
          userPassengers.find((t) => t.id === passenger.id)?.frequentFlyer
      ) {
        trackEvent(
          {
            eventName: ADD_FREQUENT_FLYER,
            properties: {
              frequent_flyer_program:
                freqFlyerKeys.length > 0
                  ? freqFlyerKeys[freqFlyerKeys.length - 1]
                  : "",
            },
          },
          apiConfig
        );
      }
      if (!allSelectedPassengerIds.includes(passenger.id)) {
        trackEvent({
          eventName: CHOOSE_TRAVELER,
          properties: chooseTravelerProperties,
        });
      }

      send({
        type: Event.UPDATE_PASSENGER,
        person: passenger,
        onUpdate,
      });
    },
    [userPassengers, allSelectedPassengerIds, send, chooseTravelerProperties]
  );

  const handleClickAddTraveler = () => {
    send(Event.OPEN_PASSENGER_FORM);
    send({ type: Event.SET_CURRENT_PASSENGER, passenger: {} as IPerson });
  };

  const handleOpenPassengerPicker = useCallback(() => {
    send(Event.OPEN_PASSENGER_PICKER);
  }, [send]);

  const handleInfantPickerModalClose = () => {
    send(Event.CLEAR_CURRENT_INFANT_ID);
  };

  const handleInfantPickerModalContinue = (seatType: "OwnSeat" | "OnLap") => {
    send({ type: Event.ON_INFANT_MODAL_CONTINUE, seatType });
    handleInfantPickerModalClose();
  };

  const headerTitle =
    openPassengerFormModal && currentPassenger?.id
      ? t("editTraveler")
      : openPassengerFormModal
        ? t("passengerInformation.addPassenger")
        : t("selectPassengers");

  const hasError = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getPassengerErrorOpen
  );

  // removing the hash when the user goes back using browser history
  useEffect(() => {
    const removeListener = history.listen((location, action) => {
      if (action === "POP") {
        if (openPassengerFormModal && !location.hash.includes(TRAVELER_HASH)) {
          handleOpenPassengerPicker();
        }
      }
    });

    return removeListener;
  }, [
    handleOpenPassengerPicker,
    history,
    openPassengerFormModal,
    removeTravelerFormHash,
    send,
  ]);

  // adding and removing hash depending on openPassengerFormModal
  useEffect(() => {
    if (openPassengerFormModal && !location.hash.includes(TRAVELER_HASH)) {
      addTravelerFormHash();
    } else if (
      !openPassengerFormModal &&
      location.hash.includes(TRAVELER_HASH)
    ) {
      removeTravelerFormHash();
    }
  }, [
    addTravelerFormHash,
    currentPassenger?.id,
    location.hash,
    openPassengerFormModal,
    removeTravelerFormHash,
  ]);

  return (
    <>
      <PassengerInformation
        isLoading={isLoading}
        loadingMessage={loadingMessage}
        userPassengers={userPassengers}
        userInfo={userContext?.sessionInfo?.userInfoResponse}
        selectedPassengerIds={allSelectedPassengerIds}
        currentPassenger={currentPassenger}
        openPassengerFormModal={openPassengerFormModal}
        openPassengerPicker={openPassengerPicker}
        desktopTopContent={<ConnectedBreadcrumbs />}
        desktopRightContent={<ConnectedPriceBreakdownContent />}
        handleCloseForm={handleCloseForm}
        handleContinue={handleContinue}
        handleDeletePassenger={handleDeletePassenger}
        handleEditClick={handleEditClick}
        handleGoBack={handleGoBack}
        handleSelectPassenger={handleSelectPassenger}
        handleUpdatePassenger={handleUpdatePassenger}
        handleClickAddTraveler={handleClickAddTraveler}
        travelerWorkflowTitles={travelerWorkflowTitles}
        handleOpenPassengerPicker={handleOpenPassengerPicker}
        currentInfantId={currentInfantId}
        handleInfantPickerModalContinue={handleInfantPickerModalContinue}
        handleInfantPickerModalClose={handleInfantPickerModalClose}
        isInfantModalLoading={isInfantModalLoading}
        tripPricing={tripPricing}
        tripCurrency={tripCurrency}
        clientAssets={clientAssets}
        isMobile={matchesMobile}
        headerTitle={headerTitle}
        ErrorBanner={<ConnectedErrorModal />}
        hasError={hasError}
        showPassport={true}
        travelerFormClassName={{
          "dark-mode": enableDarkMode && darkModePreferred,
        }}
        {...props}
      />
      <DeleteConfirmationModal
        open={!!passengerIdToDelete}
        onClose={() => setPassengerIdToDelete(null)}
        onConfirm={deletePassenger}
      />
    </>
  );
};

const DeleteConfirmationModal = ({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useI18nContext();

  return (
    <MobilePopoverCard
      centered
      open={open}
      className={clsx(
        "delete-confirmation-modal",
        styles.deleteConfirmationModal
      )}
      contentClassName="delete-confirmation-modal-content"
      onClose={onClose}
      headerElement={<h1>{t("deletePassengerModalTitle")}</h1>}
      topRightButton={
        <ActionLink
          className="close-button"
          content={
            <IconComponent
              ariaLabel="Close button icon"
              className={clsx("close-button-icon")}
              name={IconName.Close}
            />
          }
          label="Close"
          onClick={onClose}
        />
      }
    >
      <p className="description">{t("deletePassengerModalDescription")}</p>
      <div className="button-container">
        <button className="primary" onClick={onConfirm}>
          {t("deletePassengerButton")}
        </button>
        <button className="secondary" onClick={onClose}>
          {t("cancel")}
        </button>
      </div>
    </MobilePopoverCard>
  );
};

export const ConnectedErrorModal = () => {
  const { t } = useI18nContext();
  const history = useHistory();

  const [, send] = useChildMachineState<TEvent, FlightMachineContext>(
    childMachineID
  );
  const open = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getPassengerErrorOpen
  );
  const passengerError = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getPassengerError
  );
  const infantOnlyError = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getInfantOnlyError
  );

  const clearError = useCallback(
    () => send(Event.CLEAR_PASSENGER_INFORMATION_ERROR),
    [send]
  );

  const searchAgain = useCallback(() => {
    clearError();
    history.push(PATH_FLIGHTS_SEARCH);
  }, [clearError, history]);

  const openPassengerPicker = useCallback(() => {
    send(Event.OPEN_PASSENGER_PICKER);
    clearError();
  }, [clearError, send]);

  const searchPassengerCount = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getSearchPassengerNumber
  );
  const numPassengersSelected = useChildMachineSelector(
    childMachineID,
    FlightPassengerSelectors.getAllSelectedPassengerIds
  ).length;

  const showAddtlPassengerSelected = useMemo(
    () => searchPassengerCount < numPassengersSelected,
    [numPassengersSelected, searchPassengerCount]
  );

  const openPassportForm = () => send(Event.OPEN_PASSPORT_FORM);

  const errorModalProps = useMemo(() => {
    const type = passengerError?.type;

    if (infantOnlyError) {
      return {
        modal: false,
        type: "warning",
        title: t("checkoutError.invalidPassenger"),
        subtitle: t("checkoutError.checkInMinimumAgeNotMetSubtitle"),
        errorType: "infantOnly",
        primaryButtonText: t("checkoutError.updateTraveler"),
        primaryOnClick: openPassengerPicker,
      };
    }

    if (!type && showAddtlPassengerSelected) {
      return {
        modal: false,
        title: t("checkoutWarning.selectedPassengersPriceChangeSubtitle"),
        subtitle: t("checkoutWarning.selectedPassengersPriceChangeSubtitle"),
        primaryButtonText: t(
          "checkoutWarning.selectedPassengersPriceChangeSubtitle"
        ),
        primaryOnClick: null,
        className: "selected-passengers-price-change",
      };
    }
    switch (type) {
      case PassengerErrorModalTypes.TripPricing:
        return {
          modal: true,
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case PassengerErrorModalTypes.UpdatePassenger:
        return {
          modal: true,
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case PassengerErrorModalTypes.FetchPassengers:
        return {
          modal: true,
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case PassengerErrorModalTypes.ValidatePassengers:
        return {
          modal: true,
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case PassengerErrorModalTypes.NoPassengers:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.noPassengersSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassengerPicker,
        };
      case PassengerErrorModalTypes.DeletePassenger:
        return {
          modal: true,
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case PassengerErrorModalTypes.ValidateAndTripPricing:
        return {
          modal: true,
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case PassengerErrorModalTypes.ADTRequired: // todo: NoAdultPassenger
        return {
          modal: false,
          type: "warning",
          icon: Warning,
          title: t("checkoutError.noAdultPassengerTitle"),
          subtitle: t("checkoutError.noAdultPassengerSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassengerPicker,
        };
      case PassengerErrorModalTypes.INSNotAllowed: // todo: SeatedInfantsUnsupported
        return {
          title: t("checkoutError.seatedInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.seatedInfantsUnsupportedSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassengerPicker,
        };
      case PassengerErrorModalTypes.InSufficientADTCount: // todo: TooManyLapInfants
        return {
          title: t("checkoutError.tooManyLapInfantsTitle"),
          subtitle: t("checkoutError.tooManyLapInfantsSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassengerPicker,
        };
      case PassengerErrorModalTypes.UnaccompaniedMinor: // todo: TooManyLapInfants
        return {
          title: t("checkoutError.tooManyLapInfantsTitle"),
          subtitle: t("checkoutError.tooManyLapInfantsSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassengerPicker,
        };
      case PassengerErrorModalTypes.TooManyPassengers: // todo: TooManyPassengers
        return {
          title: t("checkoutError.tooManyPassengersTitle"),
          subtitle: t("checkoutError.tooManyPassengersSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassengerPicker,
        };
      case PassengerErrorModalTypes.InfantIsMinor: // todo: LapInfantTooOld
        return {
          title: t("checkoutError.lapInfantTooOldTitle"),
          subtitle: t("checkoutError.lapInfantTooOldSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: openPassengerPicker,
        };
      case PassengerErrorModalTypes.SearchPassengerNumNotReached:
        return {
          title: t("checkoutError.searchPassengerNumNotReachedTitle"),
          subtitle: t("checkoutError.searchPassengerNumNotReachedSubtitle"),
          errorType: type,
          primaryButtonText: t("checkoutError.searchPassengerNumNotReachedCta"),
          primaryOnClick: openPassengerPicker,
        };
      default:
        return {
          modal: true,
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          errorType: "default",
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
    }
  }, [
    infantOnlyError,
    openPassengerPicker,
    passengerError?.type,
    searchAgain,
    showAddtlPassengerSelected,
    t,
  ]);

  if (
    passengerError?.type === PassengerErrorModalTypes.PassportValidationFail
  ) {
    openPassportForm();
    return null;
  }

  return (
    <>
      {open && errorModalProps.modal ? (
        <ErrorPopup open={open} {...errorModalProps} />
      ) : null}
      <Snackbar
        open={(open && !errorModalProps.modal) || showAddtlPassengerSelected}
        type={errorModalProps.type}
      >
        {errorModalProps.subtitle}
      </Snackbar>
    </>
  );
};
